import { store } from '~state/ReduxWrapper';
import { getStateCode } from '~state/getStateField';
import {
  completeRegisterIneligible,
  sendRegistrationForm,
} from '~components/Register/postFinishEvents';
import { navigate } from 'gatsby';
import {
  registrationNotRequired,
  registrationPrintMailDisabled,
  registrationLeoReferralIfWillNotBe18,
  registrationNoStateFormAvailable,
} from '~components/Register/specialCases';

/**
 * This navigates to the appropriate "second step" of Futurevoter -- the screen
 * the user should see after submitting their basic biographical information.
 *
 * skipOVR: if we've already told the user about OVR, and they want to print and
 *   mail anyway, pass true
 *
 * replace: When routing to the next URL, replace the current history entry
 *   instead of pushing a new entry onto the browser history.
 */
export default function navigateToSecondStep({
  skipOVR = false,
  replace = false,
} = {}) {
  const {
    age,
    will_be_18_by_next_election: willBe18ByNextElection,
    preregistration_available: preregistrationAvailable,
    meets_age_minimum: meetsAgeMinimum,
    ovr_link: ovrLink,
  } = store.getState().futurevoterFields;
  const state = getStateCode();

  const eligibleToRegister =
    meetsAgeMinimum && (preregistrationAvailable || willBe18ByNextElection);

  if (age < 13) {
    // If they are under 13 -> Tell them to come back later
    completeRegisterIneligible('futurevoter', 'age');
  } else if (registrationNotRequired.includes(state)) {
    // If their state doesn't have voter registration (ND) -> Tell them this immediately, even if over 18
    completeRegisterIneligible('futurevoter', 'state');
  } else if (age >= 18) {
    // If over 18, we don't want to trigger the complete() function because we don't consider the action finished yet
    // (because they are still eligible for further action). Instead, we will navigate them to a page
    // where they can click through to Register, at which point a FutureVoter "finish" event will be posted.
    navigate('/tools/futurevoter/ineligible-age/', { replace });
  } else if (
    eligibleToRegister &&
    registrationPrintMailDisabled.includes(state)
  ) {
    // If there are no online or print/mail options for anyone (NH) -> Refer them to their LEO
    completeRegisterIneligible('futurevoter', 'state');
  } else if (
    eligibleToRegister &&
    !willBe18ByNextElection &&
    !ovrLink &&
    registrationNoStateFormAvailable.includes(state)
  ) {
    // If there is no online option AND there is no state print/mail option AND we don't want to send them the NVRA form
    // because they won't be 18 by Election Day (covers IL when OVR portal is shut down) -> Refer them to their LEO
    completeRegisterIneligible('futurevoter', 'state');
  } else if (
    eligibleToRegister &&
    registrationLeoReferralIfWillNotBe18.includes(state) &&
    !willBe18ByNextElection
  ) {
    // If online or print/mail options exist, but they are not set up for people who won't be 18 by Election Day
    // (DE, LA) -> Refer them to their LEO
    completeRegisterIneligible('futurevoter', 'state');
  } else if (eligibleToRegister && ovrLink && !skipOVR) {
    // If they are eligible and an OVR option is available (and they haven't already opted not to use OVR)
    // -> Present them with the option to register through OVR
    navigate('/tools/futurevoter/proceed-with-state/', { replace });
  } else if (eligibleToRegister) {
    // Any other case where they are eligible to register
    // (no special state circumstances, OVR is either unavailable or was skipped) -> Tell them we are sending them a PDF
    sendRegistrationForm(false, 'futurevoter');
  } else {
    // They are too young to take action in their state -> Display a message explaining when they will be eligible
    completeRegisterIneligible('futurevoter', 'age');
  }
}

/**
 * This handles the result of POSTing a new Futurevoter record to the backend. It
 * stores the appropriate fields of the result in Redux, then calls
 * navigateToSecondStep() to navigate the user to the next step of the
 * process.
 */
export function handleFuturevoterPostResult(
  postValues,
  postResult,
  referringTool = null
) {
  const futurevoterStateUpdate = {
    uuid: postResult.uuid || '',
    action_id: postResult.action_id || '',
    age: postResult.age || null,
    ovr_link: postResult.ovr_link || '',
    preregistration_available: postResult.preregistration_available || false,
    meets_age_minimum: postResult.meets_age_minimum || false,
    will_be_18_by_next_election:
      postResult.will_be_18_by_next_election || false,
    registration_eligibility_date:
      postResult.registration_eligibility_date || null,
    referring_tool: referringTool,
    sms_opt_in_voteamerica: postValues.sms_opt_in_voteamerica,
  };

  store.dispatch({
    type: 'FUTUREVOTER_UPDATE_FIELDS',
    futurevoterFields: futurevoterStateUpdate,
  });

  navigateToSecondStep();
}
